import { InteractionRequiredAuthError, type PublicClientApplication } from '@azure/msal-browser';
import {
  readApiError,
  VIEW_AS_READ_ONLY_MESSAGE,
  type ReadApiErrorOptions,
} from '@rr-crm/shared/api-errors';

import { apiScopes } from '../auth/msalConfig';
import { isViewingAsSalesRep } from '../auth/viewAsStorage';
import { isOnline, OfflineError } from './offline';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '';

let msalInstance: PublicClientApplication | null = null;

export function setMsalInstance(instance: PublicClientApplication): void {
  msalInstance = instance;
}

/**
 * Guards against firing more than one interactive redirect when several
 * in-flight `apiFetch` calls all hit an expired token in the same tick.
 * `acquireTokenRedirect` navigates the whole page away, so the first caller
 * wins and the rest short-circuit.
 */
let interactiveRedirectInFlight = false;

async function getAccessToken(): Promise<string | null> {
  if (!msalInstance) return null;
  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
  if (!account) return null;
  try {
    const result = await msalInstance.acquireTokenSilent({
      scopes: apiScopes,
      account,
    });
    return result.accessToken;
  } catch (err) {
    // T-CRM-AUTH-401-FIX — when the ~1h access token has expired and MSAL
    // cannot silently renew it (the hidden-iframe refresh failed, or the
    // session genuinely requires re-consent), `acquireTokenSilent` throws
    // `InteractionRequiredAuthError`. The previous `catch { return null }`
    // swallowed this and sent the request with NO Authorization header, so
    // every endpoint 401'd with no recovery path — the user appeared
    // "logged in" but the whole app was dead until a manual reload. We now
    // fall back to an interactive redirect, which re-authenticates the user
    // against Entra and returns them to the SPA with a fresh token. The
    // current in-flight request still resolves token-less (the page is
    // navigating away), but the post-redirect reload succeeds.
    if (err instanceof InteractionRequiredAuthError) {
      if (!interactiveRedirectInFlight) {
        interactiveRedirectInFlight = true;
        try {
          await msalInstance.acquireTokenRedirect({ scopes: apiScopes, account });
        } catch {
          interactiveRedirectInFlight = false;
        }
      }
      return null;
    }
    // Any other error (network blip acquiring the token, transient MSAL
    // state) — return null and let the caller surface its own error; a
    // retry will re-attempt silent acquisition.
    return null;
  }
}

/**
 * Generates a fresh correlation ID for an outbound API call (T1-010).
 *
 * Each call gets its own ID — the spec calls for one per "top-level user
 * action" but at the apiFetch boundary we don't know whether two calls came
 * from the same click or two different clicks, so we err on the side of
 * granularity: one ID per request. The backend echoes it back via the
 * `x-correlation-id` response header so a higher-level caller (TanStack
 * Query mutation, page transition tracker) can re-use a parent ID across
 * calls if it overrides the header explicitly via `init.headers`.
 *
 * Format aligns with the backend's `CORRELATION_REGEX`: alphanumeric,
 * dash, underscore; <=128 chars. `crypto.randomUUID()` satisfies this.
 */
function generateCorrelationId(): string {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID();
  }
  // Last-resort fallback for older browsers / test envs without crypto.randomUUID.
  // The backend regex accepts dashes + alphanumerics; this remains within bounds.
  return `corr-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/** Requests that change something. Everything else is a read. */
const MUTATING_METHODS: ReadonlySet<string> = new Set([
  'POST',
  'PUT',
  'PATCH',
  'DELETE',
]);

/**
 * RIG-483 — the message a blocked write produces while View As is on.
 *
 * The server already refuses these, correctly and strictly. What it cannot do
 * is explain itself in the CRM's voice: its rejection arrived as a raw
 * envelope that surfaces rendered verbatim, so a superAdmin who forgot they
 * were impersonating saw something like `{"error":"forbidden"}` and had no way
 * to connect it to the View As banner.
 */
export const VIEW_AS_WRITE_BLOCKED_MESSAGE = VIEW_AS_READ_ONLY_MESSAGE;

/**
 * QA 2026-10-01 F-28: the correlation id `apiFetch` sent with each request,
 * keyed by the Response it produced, so `throwIfNotOk` can attach it to the
 * `ApiError` even when the response carries no `x-correlation-id` header.
 */
const sentCorrelationIds = new WeakMap<object, string>();

export async function apiFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const headers = new Headers(init.headers);
  const token = await getAccessToken();
  if (token) headers.set('Authorization', `Bearer ${token}`);

  // T-VIEW-AS — while this tab is impersonating, every request carries the
  // downscope intent. The token stays the real superAdmin's (MSAL untouched);
  // the server downscopes to salesRep + enforces read-only. Per-tab only.
  if (isViewingAsSalesRep()) {
    headers.set('X-View-As-Role', 'salesRep');

    // RIG-483 — stop the write here, with a sentence a person can act on.
    //
    // Doing it at this chokepoint rather than surface by surface is the point:
    // there are dozens of write surfaces and `isViewingAsSalesRep` had exactly
    // two consumers in the whole app, so any per-surface sweep would miss some
    // and drift again. The server remains the load-bearing control; this is
    // the explanation layer in front of it.
    const method = (init.method ?? 'GET').toUpperCase();
    if (MUTATING_METHODS.has(method)) {
      return new Response(
        JSON.stringify({ error: 'view_as_read_only', message: VIEW_AS_WRITE_BLOCKED_MESSAGE }),
        { status: 403, headers: { 'content-type': 'application/json' } },
      );
    }
  }

  // RIG-484 — offline short-circuit. `api/offline.ts` shipped `isOnline`,
  // `OfflineError` and `guardOffline` and had ZERO call sites in the CRM, so
  // no mutation was offline-guarded at all: a rep on a dead connection clicked
  // Save and got the browser's raw "Failed to fetch". Reads are left alone so
  // a cached view still renders; a write that cannot possibly reach the server
  // fails with a sentence instead.
  if (MUTATING_METHODS.has((init.method ?? 'GET').toUpperCase()) && !isOnline()) {
    throw new OfflineError(`${init.method ?? 'request'} ${path}`);
  }

  // T1-010 — ensure every API request carries a correlation ID so backend
  // logs / App Insights customDimensions.correlationId / audit metadata can
  // be joined back to the originating user action. Caller-supplied header
  // wins: a TanStack Query mutation that wants several requests to share
  // the same ID can pass it explicitly.
  if (!headers.has('x-correlation-id')) {
    headers.set('x-correlation-id', generateCorrelationId());
  }

  const res = await fetch(`${API_BASE_URL}${path}`, { ...init, headers });
  const correlationId = headers.get('x-correlation-id');
  if (correlationId && res && typeof res === 'object') sentCorrelationIds.set(res, correlationId);
  return res;
}

/**
 * QA 2026-10-01 F-28: throw an `ApiError` for a non-2xx response.
 *
 * The error's `message` is always a plain sentence (`userMessage` from
 * `@rr-crm/shared/api-errors`), never the response body or the status. The
 * parsed body stays on `err.body` for screens that need `current`,
 * `existingContactId`, `details` or `issues`. Use this after `apiFetch` when a
 * caller needs the raw Response; otherwise prefer `apiJson`.
 */
export async function throwIfNotOk(res: Response, opts: ReadApiErrorOptions = {}): Promise<void> {
  if (res.ok) return;
  const sent = sentCorrelationIds.get(res);
  throw await readApiError(res, sent && !opts.correlationId ? { ...opts, correlationId: sent } : opts);
}

/**
 * QA 2026-10-01 F-28: `apiFetch` + `throwIfNotOk` + JSON. Resolves the parsed
 * body (undefined for a 204 or an empty body); rejects with `ApiError`.
 */
export async function apiJson<T>(
  path: string,
  init: RequestInit = {},
  opts: ReadApiErrorOptions = {},
): Promise<T> {
  const res = await apiFetch(path, init);
  await throwIfNotOk(res, opts);
  if (res.status === 204) return undefined as T;
  try {
    return (await res.json()) as T;
  } catch {
    return undefined as T;
  }
}
